import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getSpreadsheetLines,
  runBulkOp,
  UserKeys,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { BulkImportModel, BulkImportType } from '../../../../types';
import { Messages } from '../../../utils/message-constants';
import GeneralDialog from '../../../shared/general-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import EmptySheetPreview from '../../../shared/empty-sheet-preview';
import { useState } from 'react';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import { useBulkOpActions } from './hooks/use-bulk-op-actions';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
interface RunBulkOpsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedBulkOp: BulkImportModel;
  loggedInUserEmail: string;
  bulkImportTypes: BulkImportType[];
  changeLogRoles: string[];
}

export default function RunBulkOpsDialog({
  open,
  handleClose,
  selectedBulkOp,
  loggedInUserEmail,
  bulkImportTypes,
  changeLogRoles,
}: RunBulkOpsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const bulkImportType = bulkImportTypes?.find(
    (type) => type.name === selectedBulkOp.bulkImport
  );
  const { patchLastRan } = useBulkOpActions();

  const {
    data: spreadsheetLinesResult,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: [UserKeys.SpreadsheetLines, selectedBulkOp],
    queryFn: () => getSpreadsheetLines(selectedBulkOp),
    staleTime: 0, // Set staleTime to 0 to prevent it from being cached
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        'the bulk operations spreadsheet'
      ),
    },
  });

  const spreadsheetLines = spreadsheetLinesResult?.lines;
  const missingRequiredColumns =
    spreadsheetLinesResult?.missingRequiredColumns || [];
  const hasLoaded = !isLoading && !isFetching && !!spreadsheetLinesResult;

  const { mutate, isPending: isRunning } = useMutation({
    mutationFn: () => runBulkOp(selectedBulkOp, null, changeLogMessage),
    onSuccess: (returnData) => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.Success.Started('Bulk operation'),
      });

      patchLastRan(selectedBulkOp.id);

      if (returnData && returnData.length > 0) {
        window.open(
          `https://docs.google.com/a/${
            loggedInUserEmail.split('@')[1]
          }/spreadsheet/ccc?key=${returnData}#gid=0`,
          '_blank',
          'noopener, noreferrer'
        );
      }
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Running('this bulk operation'),
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.bulk_operations.run_bulk_operation, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.bulkOperations,
        bulkOperationType: selectedBulkOp.bulkImport,
      });
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, bulkImportType?.role || '')) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedBulkType = bulkImportTypes.find(
    (type) => type.name === selectedBulkOp.bulkImport
  )!;

  const rows: { [key: string]: string }[] = [];
  const spreadsheetHeaders = Object.keys(spreadsheetLines || {});
  if (spreadsheetHeaders.length > 0) {
    const numRows = spreadsheetLines![spreadsheetHeaders[0]].length;

    for (let i = 0; i < numRows; i++) {
      const row: { [key: string]: string } = {};
      spreadsheetHeaders.forEach((header) => {
        row[header] = spreadsheetLines![header][i];
      });
      rows.push(row);
    }
  }

  return (
    <>
      <GeneralDialog
        open={open}
        handleClose={() => {
          handleClose();
        }}
        maxWidth={'lg'}
        title="Run Bulk Operation"
        dialogContent={
          <Stack gap={2} sx={{ px: 2 }}>
            <Typography>
              {`You are about to start this ${selectedBulkType.niceName} Operation. For Email, IM, Phone
            Number, and Website columns, each must have a corresponding Relation
            column that specifies its type to be included in the import.
            Below are the first five lines from your spreadsheet. Please review
            them to ensure everything looks correct. You can view your
            spreadsheet by clicking`}{' '}
              <Link
                href={`https://docs.google.com/a/${
                  loggedInUserEmail.split('@')[1]
                }/spreadsheet/ccc?key=${selectedBulkOp.spreadsheetId}#gid=0`}
                id="anchor"
                target="_blank"
                rel="noreferrer noopener"
              >
                here.
              </Link>
            </Typography>
            <Typography>
              The sheet associated with this operation was last updated on{' '}
              {selectedBulkOp.lastExportUpdate && hasLoaded ? (
                new Date(selectedBulkOp.lastExportUpdate).toLocaleString()
              ) : (
                <Skeleton
                  variant="text"
                  width={160}
                  height={20}
                  sx={{ display: 'inline-block', mx: 0.25 }}
                />
              )}
              . If it's been a while, you may want to refresh this spreadsheet's
              data to prevent recent changes from being overwritten.
            </Typography>
            {!isLoading && !isFetching && missingRequiredColumns.length > 0 && (
              <Alert severity="error" sx={{ width: 'fit-content' }}>
                You do not have the correct column headers in your spreadsheet.
                Missing: {missingRequiredColumns.join(', ')}. See our{' '}
                <Link
                  href={
                    'https://promevo.com/knowledge/gpanel-support#bulk-operations'
                  }
                  id="anchor"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  help documentation
                </Link>{' '}
                for the required columns.
              </Alert>
            )}
            <Stack>
              <Typography variant={'caption'}>
                First 5 Rows from the Spreadsheet:
              </Typography>
              {!hasLoaded ? (
                <Skeleton
                  variant="rounded"
                  sx={{
                    height: 202,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '4px',
                    my: 2,
                  }}
                />
              ) : spreadsheetHeaders.length === 0 ? (
                <EmptySheetPreview />
              ) : (
                <TableContainer
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '4px',
                    my: 2,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {spreadsheetHeaders.map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              width: 'fit-content',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              borderRight: 1,
                              borderColor: 'divider',
                              '&:last-child': {
                                borderRight: 'none',
                              },
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={Object.values(row).join('-')}>
                          {spreadsheetHeaders.map((header) => (
                            <TableCell
                              key={header}
                              sx={{
                                width: 'fit-content',
                                whiteSpace: 'nowrap',
                                borderRight: 1,
                                borderColor: 'divider',
                                '&:last-child': {
                                  borderRight: 'none',
                                },
                              }}
                            >
                              {row[header]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Stack>
        }
        primaryButton={{
          loading: isRunning,
          action: handleSubmit,
          disabled:
            isRunning ||
            isLoading ||
            isFetching ||
            missingRequiredColumns.length > 0,
          title: 'Run',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate();
        }}
        mutateLoading={isRunning}
      />
    </>
  );
}
